import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faKey,
  faCreditCard,
  faBoxesStacked,
  faCircleInfo,
  faExternalLinkAlt,
  faRotateRight,
  faArrowUpRightFromSquare,
  faUsers,
  faBan,
} from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import AppSidebar from '../components/AppSidebar';
import WtiLoader from '../components/WtiLoader';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import {
  accountService,
  PackageCatalogItem,
  SkoolCommunityInfo,
  SubscriptionSummary,
} from '../services/accountService';
import {
  offerPlaygroundService,
  OfferPlaygroundListItem,
} from '../services/offerPlaygroundService';
import '../App.css';

type SettingsTab = 'profile' | 'security' | 'plan' | 'packages' | 'account';

function formatDate(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatLimit(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return 'Unlimited';
  if (value === 'Unlimited') return 'Unlimited';
  if (value === 0) return 'None';
  return String(value);
}

const SETTINGS_TABS: Array<{
  id: SettingsTab;
  label: string;
  description: string;
  icon: typeof faUser;
}> = [
  { id: 'profile', label: 'Profile', description: 'Name and contact details', icon: faUser },
  { id: 'security', label: 'Security', description: 'Password and sign-in', icon: faKey },
  { id: 'plan', label: 'Plan & usage', description: 'Allowance and renewal', icon: faCreditCard },
  { id: 'packages', label: 'Packages', description: 'Change or upgrade plan', icon: faBoxesStacked },
  { id: 'account', label: 'Account', description: 'Role and community', icon: faCircleInfo },
];

const Settings: React.FC = () => {
  const { user, setUserFromUpdate, clearPasswordSetup } = useAuth();
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');

  const [subscription, setSubscription] = useState<SubscriptionSummary | null>(null);
  const [packages, setPackages] = useState<PackageCatalogItem[]>([]);
  const [skoolInfo, setSkoolInfo] = useState<SkoolCommunityInfo | null>(null);
  const [loading, setLoading] = useState(true);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const [savedPlaygrounds, setSavedPlaygrounds] = useState<OfferPlaygroundListItem[]>([]);
  const [loadingPlaygrounds, setLoadingPlaygrounds] = useState(false);
  const [renamingPlaygroundId, setRenamingPlaygroundId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');

  const renewUrl = useMemo(() => {
    return (
      subscription?.renewUrl ||
      subscription?.changePackageUrl ||
      (process.env.REACT_APP_RENEWAL_URL || '').trim() ||
      'https://wheretoinvest.io/upgrade'
    );
  }, [subscription]);

  const activeMeta = SETTINGS_TABS.find((tab) => tab.id === activeTab) || SETTINGS_TABS[0];

  useEffect(() => {
    if (!user) return;
    setFirstName(user.firstName || '');
    setLastName(user.lastName || '');
    setPhone(user.phone || '');
  }, [user]);

  useEffect(() => {
    const load = async () => {
      try {
        const [summary, catalog, skool] = await Promise.all([
          accountService.getSubscriptionSummary(),
          accountService.getPackages(),
          accountService.getSkoolCommunityInfo(),
        ]);
        setSubscription(summary);
        setPackages(catalog.items || []);
        setSkoolInfo(skool);
      } catch {
        toast.error('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (activeTab !== 'account') return;
    const loadPlaygrounds = async () => {
      setLoadingPlaygrounds(true);
      try {
        const items = await offerPlaygroundService.list();
        setSavedPlaygrounds(items);
      } catch {
        toast.error('Failed to load saved Offer Playgrounds');
      } finally {
        setLoadingPlaygrounds(false);
      }
    };
    loadPlaygrounds();
  }, [activeTab]);

  const refreshPlaygrounds = async () => {
    try {
      const items = await offerPlaygroundService.list();
      setSavedPlaygrounds(items);
    } catch {
      toast.error('Failed to refresh Offer Playgrounds');
    }
  };

  const handleOpenPlayground = (id: string) => {
    window.open(`/offer-playground/${id}`, '_blank', 'noopener,noreferrer');
  };

  const handleStartRenamePlayground = (item: OfferPlaygroundListItem) => {
    setRenamingPlaygroundId(item.id);
    setRenameDraft(item.name);
  };

  const handleSaveRenamePlayground = async () => {
    if (!renamingPlaygroundId || !renameDraft.trim()) return;
    try {
      await offerPlaygroundService.renamePlayground(renamingPlaygroundId, renameDraft.trim());
      setRenamingPlaygroundId(null);
      setRenameDraft('');
      await refreshPlaygrounds();
      toast.success('Playground renamed');
    } catch {
      toast.error('Rename failed');
    }
  };

  const handleDuplicatePlayground = async (id: string) => {
    try {
      const copy = await offerPlaygroundService.duplicatePlayground(id);
      await refreshPlaygrounds();
      window.open(`/offer-playground/${copy.id}`, '_blank', 'noopener,noreferrer');
      toast.success('Playground duplicated');
    } catch {
      toast.error('Duplicate failed');
    }
  };

  const handleDeletePlayground = async (item: OfferPlaygroundListItem) => {
    if (!window.confirm(`Delete Offer Playground for "${item.address}"? This cannot be undone.`)) {
      return;
    }
    try {
      await offerPlaygroundService.deletePlayground(item.id);
      await refreshPlaygrounds();
      toast.success('Playground deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  const formatMoney = (value: number | null | undefined) => {
    if (value == null || !Number.isFinite(Number(value))) return '—';
    return `$${Math.round(Number(value)).toLocaleString()}`;
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) {
      toast.error('First and last name are required');
      return;
    }

    setSavingProfile(true);
    try {
      const result = await accountService.updateProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim() || null,
      });
      setUserFromUpdate({
        ...result.user,
        id: String(result.user.id),
        needsPasswordSetup: result.user.needsPasswordSetup ?? user?.needsPasswordSetup,
      });
      toast.success('Profile updated');
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to update profile';
      toast.error(message);
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setSavingPassword(true);
    try {
      if (user?.needsPasswordSetup) {
        const result = await authService.setPassword(newPassword);
        setUserFromUpdate(result.user);
        clearPasswordSetup();
        toast.success('Password set successfully');
      } else {
        if (!currentPassword) {
          toast.error('Current password is required');
          setSavingPassword(false);
          return;
        }
        const result = await authService.changePassword(currentPassword, newPassword);
        setUserFromUpdate(result.user);
        toast.success('Password changed successfully');
      }
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to update password';
      toast.error(message);
    } finally {
      setSavingPassword(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const result = await accountService.cancelSubscription();
      setSubscription(result.subscription);
      setConfirmCancel(false);
      toast.success(result.message);
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to cancel subscription';
      toast.error(message);
    } finally {
      setCancelling(false);
    }
  };

  const periodEndDate = formatDate(
    subscription?.accessExpiresAt ||
      subscription?.currentPeriodEnd ||
      subscription?.access?.billingEndsAt
  );

  const phaseLabel = (() => {
    if (subscription?.cancelAtPeriodEnd) return 'Cancels at period end';
    const phase = subscription?.access?.phase;
    if (phase === 'grace') return 'Grace period';
    if (phase === 'soft_locked') return 'Payment due';
    if (phase === 'active') return 'Active';
    if (subscription?.exemptFromLimits) return 'Admin access';
    return subscription?.status || 'No plan';
  })();

  if (loading) {
    return <WtiLoader fullPage message="Loading settings…" />;
  }

  return (
    <div className="wti-shell">
      <AppSidebar />

      <div className="wti-main">
        <div className="container dashboard-page settings-page">
          <header className="dashboard-header">
            <div className="dashboard-title-block">
              <h1>Settings</h1>
              <p className="dashboard-subtitle">
                Manage your profile, security, and subscription.
              </p>
            </div>
          </header>

          <div className="settings-layout">
            <nav className="settings-nav" aria-label="Settings sections">
              {SETTINGS_TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`settings-nav__item${activeTab === tab.id ? ' settings-nav__item--active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <span className="settings-nav__icon" aria-hidden="true">
                    <FontAwesomeIcon icon={tab.icon} />
                  </span>
                  <span className="settings-nav__text">
                    <span className="settings-nav__label">{tab.label}</span>
                    <span className="settings-nav__desc">{tab.description}</span>
                  </span>
                  {tab.id === 'security' && user?.needsPasswordSetup && (
                    <span className="settings-nav__dot" title="Password setup needed" />
                  )}
                </button>
              ))}
            </nav>

            <section className="card settings-panel" aria-label={activeMeta.label}>
              <div className="settings-panel__header">
                <div>
                  <h2>{activeMeta.label}</h2>
                  <p>{activeMeta.description}</p>
                </div>
                {activeTab === 'plan' && (
                  <span className={`settings-plan-badge${subscription?.cancelAtPeriodEnd ? ' settings-plan-badge--cancelling' : ''}`}>
                    {phaseLabel}
                  </span>
                )}
              </div>

              {activeTab === 'profile' && (
                <form className="settings-form settings-form--narrow" onSubmit={handleSaveProfile}>
                  <div className="settings-form-row">
                    <div className="form-group">
                      <label htmlFor="settings-first-name">First name</label>
                      <input
                        id="settings-first-name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        autoComplete="given-name"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="settings-last-name">Last name</label>
                      <input
                        id="settings-last-name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        autoComplete="family-name"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="settings-email">Email</label>
                    <input id="settings-email" value={user?.email || ''} disabled readOnly />
                    <p className="settings-hint">
                      Email cannot be changed here. Contact support if needed.
                    </p>
                  </div>

                  <div className="form-group">
                    <label htmlFor="settings-phone">Phone</label>
                    <input
                      id="settings-phone"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      autoComplete="tel"
                      placeholder="Optional"
                    />
                  </div>

                  <div className="settings-actions">
                    <button type="submit" className="btn btn-primary" disabled={savingProfile}>
                      {savingProfile ? 'Saving...' : 'Save profile'}
                    </button>
                  </div>
                </form>
              )}

              {activeTab === 'security' && (
                <form className="settings-form settings-form--narrow" onSubmit={handlePasswordSubmit}>
                  <p className="settings-panel__intro">
                    {user?.needsPasswordSetup
                      ? 'Create a password so you can sign in without a magic link.'
                      : 'Update the password you use to sign in.'}
                  </p>

                  {!user?.needsPasswordSetup && (
                    <div className="form-group">
                      <label htmlFor="settings-current-password">Current password</label>
                      <input
                        id="settings-current-password"
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        autoComplete="current-password"
                        required
                      />
                    </div>
                  )}
                  <div className="form-group">
                    <label htmlFor="settings-new-password">New password</label>
                    <input
                      id="settings-new-password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      autoComplete="new-password"
                      minLength={6}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="settings-confirm-password">Confirm new password</label>
                    <input
                      id="settings-confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                      minLength={6}
                      required
                    />
                  </div>
                  <div className="settings-actions">
                    <button type="submit" className="btn btn-primary" disabled={savingPassword}>
                      {savingPassword
                        ? 'Saving...'
                        : user?.needsPasswordSetup
                          ? 'Set password'
                          : 'Change password'}
                    </button>
                  </div>
                </form>
              )}

              {activeTab === 'plan' && (
                <>
                  {subscription?.exemptFromLimits ? (
                    <p className="settings-empty">
                      Staff accounts are not limited by package usage.
                    </p>
                  ) : !subscription?.hasSubscription ? (
                    <div className="settings-empty-block">
                      <p>{subscription?.message || 'No active subscription found.'}</p>
                      <a
                        className="btn btn-primary"
                        href={renewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Choose a package
                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                      </a>
                    </div>
                  ) : (
                    <>
                      <div className="settings-stats-grid">
                        <div className="settings-stat">
                          <p className="settings-stat__label">Current package</p>
                          <p className="settings-stat__value">
                            {subscription.displayName || '—'}
                          </p>
                        </div>
                        <div className="settings-stat">
                          <p className="settings-stat__label">Reports remaining</p>
                          <p className="settings-stat__value">
                            {subscription.limits?.reportsRemaining === null ||
                            subscription.limits?.reportsRemaining === undefined
                              ? 'Unlimited'
                              : subscription.limits.reportsRemaining}
                          </p>
                          <p className="settings-stat__hint">
                            Used {subscription.limits?.reportsUsed ?? 0} /{' '}
                            {formatLimit(subscription.limits?.reportsPerPeriodLabel)}
                          </p>
                        </div>
                        <div className="settings-stat">
                          <p className="settings-stat__label">Saved reports left</p>
                          <p className="settings-stat__value">
                            {subscription.limits?.savedReportsRemaining === null ||
                            subscription.limits?.savedReportsRemaining === undefined
                              ? 'Unlimited'
                              : subscription.limits.savedReportsRemaining}
                          </p>
                          <p className="settings-stat__hint">
                            Used {subscription.usage?.savedReportsUsed ?? 0} /{' '}
                            {formatLimit(subscription.limits?.savedReportsLimitLabel)}
                          </p>
                        </div>
                        <div className="settings-stat">
                          <p className="settings-stat__label">History access</p>
                          <p className="settings-stat__value">
                            {subscription.limits?.historyAccessLabel || '—'}
                          </p>
                        </div>
                        <div className="settings-stat">
                          <p className="settings-stat__label">Period starts</p>
                          <p className="settings-stat__value">
                            {formatDate(subscription.currentPeriodStart)}
                          </p>
                        </div>
                        <div className="settings-stat">
                          <p className="settings-stat__label">Package ends</p>
                          <p className="settings-stat__value">{periodEndDate}</p>
                          {subscription.access?.daysUntilBillingEnd != null &&
                            subscription.access.phase === 'active' && (
                              <p className="settings-stat__hint">
                                {subscription.access.daysUntilBillingEnd} day
                                {subscription.access.daysUntilBillingEnd === 1 ? '' : 's'} left
                              </p>
                            )}
                          {subscription.access?.phase === 'grace' &&
                            subscription.access.daysUntilSoftLock != null && (
                              <p className="settings-stat__hint">
                                Grace ends in {subscription.access.daysUntilSoftLock} day
                                {subscription.access.daysUntilSoftLock === 1 ? '' : 's'}
                              </p>
                            )}
                        </div>
                      </div>

                      {subscription.cancelAtPeriodEnd && (
                        <div className="settings-cancel-notice" role="status">
                          <strong>Cancellation scheduled.</strong>
                          <p>
                            You keep full access until {periodEndDate}. After that, paid
                            features pause. Renew anytime to keep the plan going.
                          </p>
                        </div>
                      )}

                      <div className="settings-actions settings-actions--split">
                        <a
                          className="btn btn-primary"
                          href={renewUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <FontAwesomeIcon icon={faRotateRight} />
                          {subscription.cancelAtPeriodEnd ? 'Renew to keep plan' : 'Renew package'}
                        </a>
                        {!subscription.cancelAtPeriodEnd && (
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setActiveTab('packages')}
                          >
                            Change / upgrade package
                          </button>
                        )}
                      </div>

                      {subscription.canCancel && !confirmCancel && (
                        <div className="settings-cancel-block">
                          <button
                            type="button"
                            className="btn btn-danger"
                            onClick={() => setConfirmCancel(true)}
                          >
                            <FontAwesomeIcon icon={faBan} />
                            Cancel subscription
                          </button>
                          <p className="settings-hint">
                            Access stays active until {periodEndDate}. You will not be billed
                            for the next period.
                          </p>
                        </div>
                      )}

                      {subscription.canCancel && confirmCancel && (
                        <div className="settings-cancel-confirm" role="alertdialog" aria-label="Confirm cancellation">
                          <strong>Cancel at the end of this period?</strong>
                          <p>
                            Your {subscription.displayName || 'plan'} stays fully active until{' '}
                            {periodEndDate}. After that date, reports and saved history pause.
                            Training remains available.
                          </p>
                          <div className="settings-actions">
                            <button
                              type="button"
                              className="btn btn-danger"
                              onClick={handleCancelSubscription}
                              disabled={cancelling}
                            >
                              {cancelling ? 'Cancelling...' : 'Confirm cancellation'}
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => setConfirmCancel(false)}
                              disabled={cancelling}
                            >
                              Keep my plan
                            </button>
                          </div>
                        </div>
                      )}

                      <p className="settings-hint">
                        Renew opens the WTI packages page to complete payment.
                      </p>
                    </>
                  )}
                </>
              )}

              {activeTab === 'packages' && (
                <>
                  <p className="settings-panel__intro">
                    Compare plans and switch anytime via the packages checkout.
                  </p>
                  <div className="settings-packages-grid">
                    {packages.map((pkg) => {
                      const isCurrent = subscription?.tierKey === pkg.tierKey;
                      return (
                        <article
                          key={pkg.tierKey}
                          className={`settings-package${isCurrent ? ' settings-package--current' : ''}`}
                        >
                          <div className="settings-package__top">
                            <h3>{pkg.displayName}</h3>
                            {isCurrent && (
                              <span className="settings-package__badge">Current</span>
                            )}
                          </div>
                          <p className="settings-package__price">{pkg.priceLabel || '—'}</p>
                          <ul className="settings-package__list">
                            <li>Reports: {formatLimit(pkg.reportsPerPeriod)}</li>
                            <li>Saved reports: {formatLimit(pkg.savedReportsLimit)}</li>
                            <li>
                              History:{' '}
                              {pkg.historyMonths === null
                                ? 'Unlimited'
                                : pkg.historyMonths === 0
                                  ? 'None'
                                  : `${pkg.historyMonths} months`}
                            </li>
                          </ul>
                          <a
                            className={`btn btn-sm ${isCurrent ? 'btn-secondary' : 'btn-primary'}`}
                            href={renewUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {isCurrent ? 'Renew' : 'Select package'}
                            <FontAwesomeIcon icon={faExternalLinkAlt} />
                          </a>
                        </article>
                      );
                    })}
                  </div>
                </>
              )}

              {activeTab === 'account' && (
                <div className="settings-account">
                  <dl className="settings-dl">
                    <div>
                      <dt>Email</dt>
                      <dd>{user?.email || '—'}</dd>
                    </div>
                    <div>
                      <dt>Role</dt>
                      <dd>{user?.role || 'USER'}</dd>
                    </div>
                    <div>
                      <dt>Status</dt>
                      <dd>{user?.status || 'ACTIVE'}</dd>
                    </div>
                    <div>
                      <dt>Member since</dt>
                      <dd>{formatDate(user?.createdAt)}</dd>
                    </div>
                    <div>
                      <dt>Current plan</dt>
                      <dd>
                        {subscription?.exemptFromLimits
                          ? 'Admin access'
                          : subscription?.displayName || 'No plan'}
                      </dd>
                    </div>
                  </dl>

                  {skoolInfo?.showSkoolCommunity && skoolInfo.skoolCommunityUrl && (
                    <div className="settings-community">
                      <div className="settings-community__copy">
                        <span className="settings-card__icon" aria-hidden="true">
                          <FontAwesomeIcon icon={faUsers} />
                        </span>
                        <div>
                          <h3>Training community</h3>
                          <p>Access WTI training and community on Skool.</p>
                        </div>
                      </div>
                      <a
                        className="btn btn-primary"
                        href={skoolInfo.skoolCommunityUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open Skool Community
                        <FontAwesomeIcon icon={faExternalLinkAlt} />
                      </a>
                    </div>
                  )}

                  <div className="settings-community" style={{ marginTop: 24 }}>
                    <div className="settings-community__copy">
                      <div>
                        <h3>Saved Offer Playgrounds</h3>
                        <p>Reopen saved offer scenarios from your WTI reports.</p>
                      </div>
                    </div>
                  </div>

                  {loadingPlaygrounds ? (
                    <WtiLoader size="sm" message="Loading saved playgrounds…" />
                  ) : savedPlaygrounds.length === 0 ? (
                    <p className="settings-empty">
                      No saved Offer Playgrounds yet. Open one from the Equity &amp; Cash Flow tab on a
                      report.
                    </p>
                  ) : (
                    <div style={{ overflowX: 'auto', marginTop: 12 }}>
                      <table style={{ width: '100%', minWidth: 720, borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #e5e7eb', textAlign: 'left' }}>
                            <th style={{ padding: 8 }}>Address</th>
                            <th style={{ padding: 8 }}>Type</th>
                            <th style={{ padding: 8 }}>WTI value</th>
                            <th style={{ padding: 8 }}>Offer price</th>
                            <th style={{ padding: 8 }}>Scenarios</th>
                            <th style={{ padding: 8 }}>Updated</th>
                            <th style={{ padding: 8 }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {savedPlaygrounds.map((item) => (
                            <tr key={item.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                              <td style={{ padding: 8 }}>
                                {renamingPlaygroundId === item.id ? (
                                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    <input
                                      type="text"
                                      value={renameDraft}
                                      onChange={(e) => setRenameDraft(e.target.value)}
                                      style={{ minWidth: 180 }}
                                    />
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-primary"
                                      onClick={handleSaveRenamePlayground}
                                    >
                                      Save
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-secondary"
                                      onClick={() => {
                                        setRenamingPlaygroundId(null);
                                        setRenameDraft('');
                                      }}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                ) : (
                                  item.address || item.name
                                )}
                              </td>
                              <td style={{ padding: 8 }}>{item.propertyType || '—'}</td>
                              <td style={{ padding: 8 }}>{formatMoney(item.wtiEstimatedValue)}</td>
                              <td style={{ padding: 8 }}>{formatMoney(item.selectedOfferPrice)}</td>
                              <td style={{ padding: 8 }}>{item.scenarioCount}</td>
                              <td style={{ padding: 8 }}>{formatDate(item.updatedAt)}</td>
                              <td style={{ padding: 8 }}>
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-primary"
                                    onClick={() => handleOpenPlayground(item.id)}
                                  >
                                    Open
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-secondary"
                                    onClick={() => handleStartRenamePlayground(item)}
                                  >
                                    Rename
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-secondary"
                                    onClick={() => handleDuplicatePlayground(item.id)}
                                  >
                                    Duplicate
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-secondary"
                                    onClick={() => handleDeletePlayground(item)}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="settings-legal-links" style={{ marginTop: 24 }}>
                    <p className="settings-hint">Legal</p>
                    <div className="settings-actions">
                      <Link to="/user-agreement" className="btn btn-secondary btn-sm" target="_blank" rel="noopener noreferrer">
                        User Agreement
                      </Link>
                      <Link to="/privacy-policy" className="btn btn-secondary btn-sm" target="_blank" rel="noopener noreferrer">
                        Privacy Policy
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
